/**
 * DSCR Quick Entry — the early-shopping sizer.
 *
 * SAME WORKBENCH AND SAME RESULTS AS THE DSCR SIZER: scenario form on the left
 * as cards, the priced answer in a sticky rail on the right, rendered by the
 * shared `DscrResultsColumn`. Both screens post the SAME endpoint
 * (`POST /pricing/scenario/dscr_quick`), so one answer surface is the point —
 * they had drifted into two readouts of one payload, and the old one was still
 * reading a `sensitivity` block the backend retired on 2026-08-18.
 *
 * WHAT STAYS DIFFERENT is the QUESTIONS, which is this screen's whole identity:
 * a SINGLE rent override, no per-unit grid, no vacancy basis, no provenance
 * gathering. Deliberately the opposite of the DSCR Calculations workbook on the
 * drilldown — that one exists to attach evidence and haircuts to every input;
 * this one exists to get a number fast before any documents are in. Anything
 * priced here is a stated estimate.
 *
 * "Lighter" means FEWER QUESTIONS, never wrong answers. The inputs that actually
 * move the price are all here: the canonical `amortizationType` (from which
 * `amortizing` derives), the prepay structure, citizenship (the sheet's Foreign
 * National row prices without a FICO), the correspondent margin — and now the
 * property address, which the endpoint GATES on (422 without `address.zip`,
 * because market data and the CFPB rural determination embed into the price).
 * What quick mode drops is per-unit detail, not LLPAs.
 *
 * RENDER-ONLY. Every number traces to a response field; no DSCR, PITIA or price
 * math happens here. Changing an input means re-POSTing, not recomputing.
 */
import { useCallback, useEffect, useMemo, useRef, useState } from "react";

import Box from "@mui/material/Box";
import CircularProgress from "@mui/material/CircularProgress";
import CalculateIcon from "@mui/icons-material/Calculate";
import { useSnackbar } from "notistack";

import Flx from "../../../components/layout/Flx";
import Txt from "../../../components/typography/Txt";
import { dscrApi } from "../../../api/dscrApi";
import { formatDateTime } from "../../../_src_shared/utils/functions/formatDateTime";
import useSizerCorrespondents from "../../sizersShared/useSizerCorrespondents";
import DscrResultsColumn from "../../sizersShared/DscrResultsColumn";
import DscrEntryTabs from "../../sizersShared/DscrEntryTabs";
import SizerAddressInput from "../../sizersShared/SizerAddressInput";
import SizerSelectAutocomplete from "../../sizersShared/SizerSelectAutocomplete";
import {
  SZ,
  SizerShell,
  Workbench,
  ResultsCol,
  FormCard,
  Section,
  Grid,
  Field2,
  ChipGroup,
  DelayedPurchaseChips,
  NumInput,
  MoneyInput,
  PercentInput,
  SelectInput,
  TextInput,
  FixedActionBar,
  PrimaryButton,
  SecondaryButton,
  ActionNote,
  SizerDevBar,
  useSizerDevTools,
  SizerStateSpy,
  scrollSizerToTop,
} from "../../sizersShared/sizerUi";
import {
  Callout,
  MissingRequirementsCallout,
} from "../../sizersShared/sizerResultUi";
/* ── Selections come from ONE place, shared with the other two sizers ─────────
 * `sizersShared/sizerSelectOptions` re-exports the portal's canonical
 * `constants/selectOptions/*` lists, so `loanPurpose` and `propertyType` render
 * here exactly as they do on the Loan Drilldown and the create-loan form.
 */
import {
  selectOptionsLoanPurpose,
  selectOptionsPropertyType,
  dscrOptionsPrepaymentPenaltyStructure,
  selectOptionsCitizenshipStatus,
  dscrOptionsAmortizationType,
  sizerOptionsArmProduct,
  dscrSheetLabel,
} from "../../sizersShared/sizerSelectOptions";
import { unitsForPropertyType } from "../../../constants/selectOptions/unitsByPropertyType";
import {
  DELAYED_PURCHASE_HINT,
  DELAYED_PURCHASE_LABEL,
  canBeDelayedPurchase,
  isPurchaseLike,
} from "../../../utils/loans/delayedPurchase";

/**
 * The form opens EMPTY — no sample amounts, no assumed product. The exceptions
 * are the inputs whose "unset" is not a real state: the qualifying-payment
 * basis, the ladder column and the citizenship default all have a defined value
 * on the input model, and echoing it is more honest than a blank that silently
 * becomes it.
 */
const DEFAULTS = {
  // REQ-106: asked only on a cash-out refi.
  delayedPurchase: null,
  resolvedCreditScore: "",
  requestedLoanAmount: "",
  requestedCoupon: "",
  loanPurpose: "",
  propertyType: "",
  address: {},
  armProduct: "fixed_30",
  amortizationType: "30-Year Fixed",
  prepaymentPenaltyStructure: "",
  citizenshipStatus: "US Citizen",
  correspondentId: null,
  monthlyRent: "",
  asIsValue: "",
  purchasePrice: "",
  annualTaxes: "",
  annualInsurance: "",
};

// Sample scenario behind the dev-only "Fill demo scenario" button. Every
// selection is a value the field actually offers. Rents are sized so the demo
// clears BOTH DSCR pricing tiers across the ladder — see dscrConstants for why
// a sub-1.0 demo made the Alternatives panel render empty.
const DEMO = {
  ...DEFAULTS,
  resolvedCreditScore: 740,
  requestedLoanAmount: 500000,
  requestedCoupon: 0.0725,
  loanPurpose: "Purchase",
  propertyType: "SFR",
  // Real address, no coordinates: lat/lng are a geocode RESULT the Places picker
  // writes, and the rural check runs a polygon lookup straight off them.
  address: {
    fullAddress: "1200 Brickell Ave, Miami, FL 33131, USA",
    streetNumber: "1200",
    streetName: "Brickell Avenue",
    city: "Miami",
    state: "FL",
    zip: "33131",
    county: "Miami-Dade County",
  },
  prepaymentPenaltyStructure: "5% 4% 3% 2% 1%",
  monthlyRent: 5200,
  asIsValue: 700000,
  purchasePrice: 700000,
  annualTaxes: 8000,
  annualInsurance: 2000,
};

/* Both narrowed lists this form uses — amortization and prepay — now live in
   `sizerSelectOptions` and are shared with the full DSCR sizer. Quick entry had
   its own copy of the amortization one while the full form still offered
   "Interest Only"; two narrowings of the same field on two forms of the same
   endpoint is exactly how the offered set drifts, so there is one. */

const num = (v) => (v === "" || v == null ? null : Number(v));
const num0 = (v) => (v === "" || v == null ? 0 : Number(v));

/**
 * Flatten the 422 body into `{fieldName: note}`.
 *
 * TWO SHAPES, both legitimate: the LLPA gates arrive as
 * `{llpa, missing: [names], note}`, the newer input gates (`address.zip`) as
 * `{field, reason}`. Reading only the first is what left the ZIP gate with
 * nothing to attach to.
 */
function gateFieldsFrom(missing) {
  return (missing || []).reduce((acc, m) => {
    (m.missing || []).forEach((f) => {
      acc[f] = m.note;
    });
    if (m.field) acc[m.field] = m.reason || m.note;
    return acc;
  }, {});
}

export default function DscrQuickEntrySizer() {
  const { enqueueSnackbar } = useSnackbar();
  const [form, setForm] = useState(DEFAULTS);
  const [result, setResult] = useState(null);
  const [pricedAt, setPricedAt] = useState(null);
  const [pricedForm, setPricedForm] = useState(null);
  const [missingRequirements, setMissingRequirements] = useState([]);
  // The rural 422's body — a determination plus the market block, not an error.
  const [ruralGate, setRuralGate] = useState(null);
  const [ruralNote, setRuralNote] = useState("");
  const [busy, setBusy] = useState(false);
  // The ladder rung a click-to-re-price is in flight for — drives the spinner ON
  // that row, so the feedback is where the click was.
  const [reanchoring, setReanchoring] = useState(null);
  const shellRef = useRef(null);
  const correspondents = useSizerCorrespondents();
  const { spyOpen, toggleSpy } = useSizerDevTools();
  // The persisted scenario this screen is pricing. Sending the id back is what
  // makes the next run a VERSION of it rather than a new object. A ref because
  // it must survive a reprice without being a render input.
  const scenarioIdRef = useRef(null);

  const set = (k, v) => setForm((prev) => ({ ...prev, [k]: v }));

  // Typing a ZIP by hand is the fallback path, so it edits ONLY the zip and
  // leaves whatever the picker resolved alone — clearing a resolved street
  // address because someone corrected a ZIP would throw away the address-level
  // rural determination that address bought.
  const setZip = (zip) => set("address", { ...(form.address || {}), zip });

  // A purchase REQUIRES purchasePrice (the input model's own validator) — a
  // refinance makes it the optional original purchase price.
  // A delayed purchase counts (REQ-106 parity rule).
  const isPurchase = isPurchaseLike(form.loanPurpose, form.delayedPurchase);
  const delayedUnanswered =
    canBeDelayedPurchase(form.loanPurpose) &&
    typeof form.delayedPurchase !== "boolean";

  /**
   * Form state → `DscrQuickPriceInput` + routing extras.
   *
   * The one quick-mode shape change: a SINGLE rent override stands in for BOTH
   * rollups. There is no per-unit detail at this stage, so in-place and market
   * are the same number and the vacancy basis is Leased — stated, not derived.
   */
  const buildPayload = useCallback((f, rural) => {
    const rent = num0(f.monthlyRent);
    const address = f.address || {};
    const hasAddress = Object.keys(address).some(
      (k) => address[k] !== "" && address[k] != null,
    );
    return {
      loanPurpose: f.loanPurpose,
      delayedPurchase: canBeDelayedPurchase(f.loanPurpose)
        ? typeof f.delayedPurchase === "boolean"
          ? f.delayedPurchase
          : null
        : null,
      requestedLoanAmount: num0(f.requestedLoanAmount),
      asIsValue: num0(f.asIsValue),
      purchasePrice: num(f.purchasePrice),
      resolvedCreditScore: num(f.resolvedCreditScore),
      // AT LEAST address.zip or the endpoint 422s — market data and the rural
      // determination embed into the price. A full address resolves rural at
      // ADDRESS level; a bare ZIP falls back to the county crosswalk.
      address: hasAddress ? address : null,
      zipCode: address.zip || null,
      // Null anchors the result at best execution instead — a real choice.
      requestedCoupon: num(f.requestedCoupon),
      // The type decides the units (the backend overrides a disagreeing
      // count, and the 2–4 / 5–9 unit pricing adjustments key off it). Only
      // Multifamily / Mixed-Use / Commercial take an entered number.
      propertyType: f.propertyType || null,
      unitCount:
        unitsForPropertyType(f.propertyType) ?? (num0(f.unitCount) || 1),
      totalRents: rent,
      totalMarketRents: rent,
      vacancySelection: "Leased",
      annualTaxes: num0(f.annualTaxes),
      annualInsurance: num0(f.annualInsurance),
      annualFloodInsurance: 0,
      annualHoaFees: 0,
      annualOtherExpense: 0,
      // CANONICAL: `amortizing` is derived from this server-side.
      amortizationType: f.amortizationType,
      armProduct: f.armProduct,
      // Unset = the sheet's "No Prepayment Penalty" row + max-price clamp, which
      // is a real pricing outcome rather than a missing input.
      prepaymentPenaltyStructure: f.prepaymentPenaltyStructure || null,
      condo: f.propertyType === "Condo",
      nonWarrantableCondo: f.propertyType === "Non-Warrantable Condo",
      crossCollateralized: false,
      citizenshipStatus: f.citizenshipStatus || null,
      correspondentId: f.correspondentId || undefined,
      tier: "BASE",
      // RURAL GATE: never defaulted on — the flag is only ever set by the user
      // acting on the gate, and it rides onto the persisted scenario.
      ...(rural?.requested
        ? {
            ruralExceptionRequested: true,
            ruralExceptionNote: rural.note || null,
          }
        : null),
    };
  }, []);

  const run = useCallback(
    (rural, overrideForm) => {
      const f = overrideForm || form;
      setBusy(true);
      setMissingRequirements([]);
      if (!rural) setRuralGate(null);
      dscrApi
        .quickPrice({
          ...buildPayload(f, rural),
          ...(scenarioIdRef.current
            ? { scenarioId: scenarioIdRef.current }
            : null),
        })
        .then((r) => {
          // Persistence is best-effort server-side: no id back means the run
          // wasn't stored, and the next price starts a new scenario.
          if (r?.scenarioId) scenarioIdRef.current = r.scenarioId;
          setResult(r);
          setRuralGate(null);
          setPricedForm(f);
          setPricedAt(new Date().toISOString());
        })
        .catch((err) => {
          const body = err?.response?.data;
          const status = err?.response?.status;
          if (status === 422 && body?.ruralGate) {
            // Not an error — a determination that blocks pricing until an
            // exception is requested. It carries the rural check and the market
            // block, so the rail renders them while the form asks for the note.
            setRuralGate(body.ruralGate);
            setResult(null);
          } else if (status === 422 && body?.missingRequirements) {
            // A gate, not a failure: these inputs would otherwise skip an LLPA
            // table and misprice. Surfaced on the fields, not as a toast.
            setMissingRequirements(body.missingRequirements);
            setResult(null);
          } else {
            enqueueSnackbar(body?.detail || "Quick price failed", {
              variant: "error",
            });
          }
        })
        .finally(() => {
          setBusy(false);
          setReanchoring(null);
        });
    },
    [form, buildPayload, enqueueSnackbar],
  );

  /**
   * Re-anchor on a ladder click: price THIS rung.
   *
   * RE-POSTS rather than re-deriving — each rung's LLPA bucket, clamp and DSCR
   * band are the server's to decide. `setForm` is async, so the next form is
   * passed explicitly or the request would carry the previous coupon.
   */
  const reanchor = (coupon) => {
    const next = { ...form, requestedCoupon: coupon };
    setForm(next);
    setReanchoring(coupon);
    run(null, next);
  };

  // A run reflows the page from a button that may be most of a screen down. Put
  // the top back in view so the answer is read from its first line. Keyed on
  // `pricedAt`, stamped fresh every run, so a re-price scrolls again.
  useEffect(() => {
    if (pricedAt) scrollSizerToTop(shellRef.current);
  }, [pricedAt]);

  const gate = gateFieldsFrom(missingRequirements);
  const GateError = ({ name }) =>
    gate[name] ? (
      <Txt sx={{ fontSize: 11.5, color: SZ.bad, mt: "5px" }}>{gate[name]}</Txt>
    ) : null;

  const pricedContext = useMemo(() => {
    const r = result?.result || {};
    return [
      (pricedForm || {}).loanPurpose,
      r.tier ? `${r.tier} tier` : null,
      r.cpId ? `priced off ${dscrSheetLabel(r.cpId)}` : null,
      result?.scenarioVersion ? `v${result.scenarioVersion}` : null,
    ]
      .filter(Boolean)
      .join(" · ");
  }, [pricedForm, result]);

  const correspondentOptions = [
    { value: "", label: "Eastview base" },
    ...correspondents,
  ];

  return (
    <SizerShell
      headerAction={
        <SizerDevBar
          variant="menu"
          /* A different scenario, not a revision of the one on screen — drop
             the persisted id so the next price starts its own history. */
          onFillDemo={() => {
            scenarioIdRef.current = null;
            setForm(DEMO);
          }}
          spyOpen={spyOpen}
          onToggleSpy={toggleSpy}
        />
      }
      rootRef={shellRef}
      title="DSCR Sizer"
      subtitle="A lighter pass for early shopping — one rent figure, estimates only."
      tabs={<DscrEntryTabs />}
    >
      <Workbench>
        <Box
          component="form"
          onSubmit={(e) => {
            e.preventDefault();
            if (!delayedUnanswered) run();
          }}
        >
          {/* Plain cards, never collapsible ones — the same call the other two
              sizers make. Pricing is an iterate loop, so the inputs stay put and
              stay open beside the answer. Two cards rather than the DSCR sizer's
              four: quick entry asks two questions — what the loan is, and what
              the property earns. */}
          <Flx column g={2.5}>
            <FormCard>
              <Section
                first
                title="Loan & borrower"
                subtitle="Single rent override, no provenance gathering — flagged downstream as estimates."
              >
                <Grid cols={12}>
                  <Field2 label="Purpose" span={7} required>
                    <ChipGroup
                      sm
                      value={form.loanPurpose}
                      onChange={(v) => {
                        set("loanPurpose", v);
                        if (!canBeDelayedPurchase(v)) set("delayedPurchase", null);
                      }}
                      options={selectOptionsLoanPurpose}
                    />
                  </Field2>
                  <Field2
                    label="Rate structure"
                    span={5}
                    tip="The coupon-ladder column on the pricing sheet."
                  >
                    <ChipGroup
                      sm
                      value={form.armProduct}
                      onChange={(v) => set("armProduct", v)}
                      options={sizerOptionsArmProduct}
                    />
                  </Field2>
                </Grid>

                {/* REQ-106: delayed purchase is a boolean on a cash-out refi,
                    not a purpose. Required while it shows. */}
                {canBeDelayedPurchase(form.loanPurpose) && (
                  <Grid cols={1} sx={{ mt: "24px" }}>
                    <Field2
                      label={DELAYED_PURCHASE_LABEL}
                      required
                      hint={DELAYED_PURCHASE_HINT}
                    >
                      <DelayedPurchaseChips
                        value={form.delayedPurchase}
                        onChange={(v) => set("delayedPurchase", v)}
                      />
                    </Field2>
                  </Grid>
                )}

                <Grid cols={1} sx={{ mt: "24px" }}>
                  <Field2 label="Amortization" required>
                    <ChipGroup
                      sm
                      value={form.amortizationType}
                      onChange={(v) => set("amortizationType", v)}
                      options={dscrOptionsAmortizationType}
                    />
                  </Field2>
                </Grid>

                <Grid cols={3} sx={{ mt: "26px" }}>
                  <Field2 label="Loan amount" required>
                    <MoneyInput
                      value={form.requestedLoanAmount}
                      onChange={(v) => set("requestedLoanAmount", v)}
                    />
                  </Field2>
                  <Field2
                    label="Requested rate"
                    tip="Leave blank to anchor the answer at best execution — the highest-premium passing rung."
                  >
                    <PercentInput
                      value={form.requestedCoupon}
                      onChange={(v) => set("requestedCoupon", v)}
                      factor={100}
                      placeholder="7.250"
                    />
                  </Field2>
                  <Field2 label="FICO" required>
                    <NumInput
                      value={form.resolvedCreditScore}
                      onChange={(v) => set("resolvedCreditScore", v)}
                    />
                    <GateError name="resolvedCreditScore" />
                  </Field2>
                </Grid>

                <Grid cols={2} sx={{ mt: "26px" }}>
                  <Field2
                    label="Prepay penalty"
                    tip="Leave unset to price the sheet's own No Prepayment Penalty row and its max-price clamp."
                  >
                    <SelectInput
                      value={form.prepaymentPenaltyStructure}
                      onChange={(v) => set("prepaymentPenaltyStructure", v)}
                      options={dscrOptionsPrepaymentPenaltyStructure}
                      placeholder="No prepayment penalty"
                    />
                  </Field2>
                  <Field2
                    label="Citizenship status"
                    tip="Foreign National prices off the sheet's own FN row — no credit score needed."
                  >
                    <SelectInput
                      value={form.citizenshipStatus ?? ""}
                      onChange={(v) => set("citizenshipStatus", v)}
                      options={selectOptionsCitizenshipStatus}
                    />
                  </Field2>
                </Grid>

                {/* Type-or-browse: this list grows with every counterparty
                    onboarded, and a plain dropdown makes finding one a scroll. */}
                <Grid cols={1} sx={{ mt: "26px" }}>
                  <Field2
                    label="Correspondent"
                    tip="Optional — resolves the margin exactly the way the rate lock does."
                  >
                    <SizerSelectAutocomplete
                      value={form.correspondentId ?? ""}
                      onChange={(v) => set("correspondentId", v || null)}
                      options={correspondentOptions}
                      placeholder="Search correspondents"
                      noOptionsText="No correspondent matches"
                    />
                  </Field2>
                </Grid>
              </Section>
            </FormCard>

            <FormCard>
              <Section
                first
                title="Property & income"
                subtitle="One rent figure stands in for both in-place and market at this stage."
              >
                <Grid cols={12}>
                  <Field2
                    label="Property address"
                    span={8}
                    tip="A full address resolves the CFPB rural check at address level (HouseCanary census / census.gov FIPS). ZIP alone falls back to the county crosswalk."
                  >
                    <SizerAddressInput
                      value={form.address || {}}
                      onChange={(next) => set("address", next)}
                    />
                  </Field2>
                  <Field2 label="ZIP" span={4} required>
                    <TextInput
                      mono
                      inputMode="numeric"
                      maxLength={5}
                      value={form.address?.zip || ""}
                      onChange={(e) => setZip(e.target.value)}
                      placeholder="33131"
                    />
                    <GateError name="address.zip" />
                  </Field2>
                </Grid>

                {(form.address?.city ||
                  form.address?.county ||
                  form.address?.state) && (
                  <Flx
                    g={0.75}
                    sx={{ mt: "8px", flexWrap: "wrap", alignItems: "baseline" }}
                  >
                    <Txt sx={{ fontSize: 11.5, color: SZ.ink400 }}>
                      Resolved
                    </Txt>
                    <Txt
                      sx={{ fontSize: 11.5, color: SZ.ink700, fontWeight: 500 }}
                    >
                      {[
                        form.address.city,
                        form.address.county,
                        form.address.state,
                      ]
                        .filter(Boolean)
                        .join(" · ")}
                    </Txt>
                  </Flx>
                )}

                <Grid cols={3} sx={{ mt: "26px" }}>
                  {/* Sent as `propertyType`: the backend derives the unit
                      count and the condo flags from it. Not required. A type
                      with no encoded count (Multifamily / Mixed-Use /
                      Commercial) asks for Units below. */}
                  <Field2 label="Property type">
                    <SelectInput
                      value={form.propertyType}
                      onChange={(v) => set("propertyType", v)}
                      options={selectOptionsPropertyType}
                      placeholder="Select property type"
                    />
                  </Field2>
                  {/* Only for a type with no encoded count (Multifamily /
                      Mixed-Use / Commercial) — every other type sets the units. */}
                  {form.propertyType &&
                    unitsForPropertyType(form.propertyType) == null && (
                      <Field2 label="Units">
                        <NumInput
                          value={form.unitCount}
                          onChange={(v) => set("unitCount", v)}
                        />
                      </Field2>
                    )}
                  <Field2 label="Appraisal / as-is value" required>
                    <MoneyInput
                      value={form.asIsValue}
                      onChange={(v) => set("asIsValue", v)}
                    />
                  </Field2>
                  {/* CONDITIONALLY required, and it matters more than the other
                      gates: a purchase without a price fails the input model's
                      own validator, which comes back as a 400 — not the friendly
                      422 the field gates use — so it would surface as a snackbar
                      with nothing attached to the field. Mark it here instead. */}
                  <Field2
                    label="Purchase price"
                    required={isPurchase}
                    tip={
                      isPurchase
                        ? "Required on a purchase — leverage prices off the LOWER of the appraised value and the purchase price."
                        : "Optional on a refinance — the original purchase price, basis context only."
                    }
                  >
                    <MoneyInput
                      value={form.purchasePrice}
                      onChange={(v) => set("purchasePrice", v)}
                    />
                  </Field2>
                </Grid>

                <Grid cols={3} sx={{ mt: "26px" }}>
                  <Field2
                    label="Monthly rent"
                    required
                    tip="Stands in for BOTH in-place and market rent at this stage — the vacancy basis is Leased."
                  >
                    <MoneyInput
                      value={form.monthlyRent}
                      onChange={(v) => set("monthlyRent", v)}
                    />
                    <GateError name="totalRents" />
                  </Field2>
                  <Field2 label="Annual taxes">
                    <MoneyInput
                      value={form.annualTaxes}
                      onChange={(v) => set("annualTaxes", v)}
                    />
                  </Field2>
                  <Field2 label="Annual insurance">
                    <MoneyInput
                      value={form.annualInsurance}
                      onChange={(v) => set("annualInsurance", v)}
                    />
                  </Field2>
                </Grid>
              </Section>
            </FormCard>
          </Flx>

          {missingRequirements.length > 0 && (
            <Box sx={{ mt: 2.5 }}>
              <MissingRequirementsCallout items={missingRequirements} />
            </Box>
          )}

          {/* THE RURAL GATE'S ACTION lives with the form, because that is where
              the re-POST is. The determination itself renders in the rail. The
              note is required by the contract (who is requesting, and why) and
              rides onto the persisted scenario. */}
          {ruralGate && (
            <Box sx={{ mt: 2.5 }}>
              <Callout
                tone="warn"
                title="Rural property — exception required to price"
              >
                <Box sx={{ mb: 1.25 }}>{ruralGate.proceed}</Box>
                <Box
                  component="textarea"
                  rows={2}
                  value={ruralNote}
                  onChange={(e) => setRuralNote(e.target.value)}
                  placeholder="Who is requesting the exception, and why"
                  sx={{
                    width: "100%",
                    resize: "vertical",
                    fontFamily: "inherit",
                    fontSize: 13,
                    color: SZ.ink900,
                    background: SZ.card,
                    border: `1px solid ${SZ.lineStrong}`,
                    borderRadius: SZ.radiusSm,
                    p: "8px 10px",
                    "&:focus": {
                      outline: "none",
                      borderColor: SZ.teal500,
                      boxShadow: "0 0 0 3px rgba(20,160,170,.15)",
                    },
                  }}
                />
                <Flx ac g={1.5} sx={{ mt: 1.25, flexWrap: "wrap" }}>
                  <SecondaryButton
                    disabled={busy || !ruralNote.trim()}
                    onClick={() =>
                      run({ requested: true, note: ruralNote.trim() })
                    }
                  >
                    Request exception &amp; price
                  </SecondaryButton>
                  <ActionNote>
                    The exception is stored on the scenario and follows the
                    loan.
                  </ActionNote>
                </Flx>
              </Callout>
            </Box>
          )}

          <FixedActionBar alignTo={shellRef}>
            <Flx
              jb
              g={2.5}
              sx={{ width: "100%", alignItems: "center", flexWrap: "wrap" }}
            >
              <Flx column g={0.25} sx={{ flex: "1 1 240px", minWidth: 0 }}>
                <ActionNote>
                  {delayedUnanswered
                    ? "Answer Delayed purchase? to price a cash-out refinance."
                    : "Priced off the saved DSCR pricing config — estimates only."}
                </ActionNote>
              </Flx>

              <Flx
                ac
                g={1.75}
                sx={{
                  flex: "1 1 340px",
                  justifyContent: "flex-end",
                  flexWrap: "wrap",
                }}
              >
                <PrimaryButton
                  type="submit"
                  disabled={busy || delayedUnanswered}
                  sx={{ flex: 1, minWidth: 200, maxWidth: 340 }}
                  startIcon={
                    busy ? (
                      <CircularProgress size={16} sx={{ color: "#fff" }} />
                    ) : (
                      <CalculateIcon />
                    )
                  }
                >
                  {busy
                    ? "Pricing…"
                    : result
                      ? "Re-price scenario"
                      : "Run quick price"}
                </PrimaryButton>
              </Flx>
            </Flx>
          </FixedActionBar>

          {/* Dev tooling last, under the form it acts on — not in the page
              header, which is about the scenario. */}

          {/* Live scenario state — admin only, off until the Form spy button
              above turns it on. This screen isn't react-final-form, so it uses
              the plain-object twin of RffFormSpy. */}
          <SizerStateSpy values={form} enabled={spyOpen} />
        </Box>

        <ResultsCol>
          <Flx column g={2}>
            {pricedAt && result && (
              <Txt sx={{ fontSize: 11, color: SZ.ink400 }}>
                Priced {formatDateTime(pricedAt)}
                {pricedContext ? ` · ${pricedContext}` : ""}
              </Txt>
            )}

            <DscrResultsColumn
              result={result}
              pending={busy}
              ruralGate={ruralGate}
              zip={form.address?.zip}
              onReanchor={reanchor}
              pendingCoupon={reanchoring}
            />
          </Flx>
        </ResultsCol>
      </Workbench>
    </SizerShell>
  );
}
